import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import { lensPath, set } from 'ramda';

import DiagnosticForm from './Form';
import Title from '../shared/Title';
import Text from '../shared/Text';
import { fillSpace, breakpoints, palette } from '../../constants/styles';
import { getQuestions, stringToNumber, getGoalNames, iOSSafari } from '../../constants/utils';
import ProgressBar from '../shared/ProgressBar';

import {
  HEADER_HEIGHT,
  CustomFlowHeader,
  StyledUpperHeader,
  StyledBackButton,
} from '../GoalsFlow/styles';
import StepCounter from '../shared/StepCounter';
import Icon from '../shared/Icon';
import FlowHeader from '../shared/FlowHeader';

const SCROLL_OFFSET = 350;

const StyledDiagnosticFlow = styled.div`
  display: flex;
  flex-direction row;
  ${fillSpace}
`;

const FlowContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  margin-top: ${HEADER_HEIGHT}px;
  background-color: white;

  width: 100%;
`;

const OverflowBanner = styled.div`
  z-index: 1;
  background-color: rgba(255, 103, 0, 1);
  color: #ffffff;
  position: fixed;
  width: 100%;
  padding: 1em;
  height: fit-content;
  * {
    color: #ffffff;
  }
`;

const FlowContent = styled.div`
  display: flex;
  align-items: center;
  height: calc(100vh - 50px);
  padding: 0 20px;
  ${breakpoints.tablet} {
    width: 70%;
    margin-left: 15%;
    margin-right: 15%;
  }

  ${breakpoints.tabletLandscape} {
    width: 60%;
    margin-left: 20%;
    margin-right: 20%;
  }
`;

function findQuestionFromId(id, questionnaire) {
  let questionFound;
  questionnaire.forEach((questionBlock) => {
    questionBlock.questions.forEach((question) => {
      if (question.inputName === id) {
        questionFound = question;
      }
    });
  });
  return questionFound;
}

class DiagnosticFlow extends Component {
  state = {
    isFinished: false,
    questionnaire: [],
    focusedGoal: 0,
    focusedQuestion: 0,
    questionsAnswered: [],
    totalQuestions: 0,
    inputList: [],
    financialDiagnostic: [],
    userAge: null,
    sectionExpenses: 0,
    currentValue: 0,
    income: 0,
    expenseMap: {},
    expenseRate: 1,
  };

  componentDidMount = () => {
    this.initializeForm(this.props);
    this.props.mixpanel();
    this.setQuestionAsFocused();
    document.addEventListener('keyup', this.keyPress, false);
    this.setState({
      sectionExpenses: this.props.globalExpenses,
      income: this.props.globalIncome,
      expenseMap: this.props.expenseMap,
    });
  };

  componentDidUpdate = () => {
    //
  };

  componentWillReceiveProps(nextProps) {
    if (this.props !== nextProps) {
      this.initializeForm(nextProps);
    }
  }

  componentWillUnmount() {
    document.removeEventListener('keyup', this.keyPress, false);
  }

  setGoalAsFocused = (goalIndex = 0) => {
    this.setState((state) => ({
      ...set(lensPath(['questionnaire', goalIndex, 'isFocused']), true, state),
      focusedGoal: goalIndex,
    }));
  };

  setGoalAsAnswered = (goalIndex = 0) => {
    this.setState((state) => ({
      ...set(lensPath(['questionnaire', goalIndex, 'isAnswered']), true, state),
    }));
  };

  setQuestionAsFocused = (goalIndex = 0, questionIndex = 0) => {
    this.setGoalAsFocused(goalIndex);
    this.setState((state) => ({
      ...set(
        lensPath(['questionnaire', goalIndex, 'questions', questionIndex, 'isFocused']),
        true,
        state,
      ),
      focusedGoal: goalIndex,
      focusedQuestion: questionIndex,
    }));
  };

  setAllAsBlur = (cb) => {
    const { questionnaire } = this.state;
    this.setState(
      {
        ...this.state,
        questionnaire: questionnaire.map((goal) => ({
          ...goal,
          isFocused: false,
          questions: goal.questions.map((question) => ({
            ...question,
            isFocused: false,
          })),
        })),
      },
      cb,
    );
  };

  setGoalAnswers = (values, goalName, goalIndex) => {
    const { userAge } = this.state;
    this.setState((state) => ({
      ...set(lensPath(['financialDiagnostic', goalIndex]), values, state),
    }));
  };

  formatValues = (values) => {
    const { age, expectedAge, moneyRequired, initialSavings, ...rest } = values;
    const yearstoExpense = expectedAge - age;

    return {
      ...rest,
      age,
      current_savings: stringToNumber(initialSavings),
      money_required: stringToNumber(moneyRequired),
      years_to_expense: yearstoExpense || 0,
    };
  };

  initializeForm = ({ questionnaire }, cb) => {
    const maxQuestions = 0;
    this.setState(
      {
        questionnaire,
        totalQuestions: getQuestions(questionnaire).length + maxQuestions,
        goalNames: getGoalNames(questionnaire),
        financialDiagnostic: [],
        maxQuestions,
      },
      () => {
        this.updateInputList(cb);
      },
    );
  };

  updateInputList = (cb) => {
    const inputsFound = document.querySelectorAll('.question');
    const inputList = [];
    inputsFound.forEach((input) => {
      inputList.push(input.id);
    });
    this.setState({ inputList }, cb);
  };

  injectNewQuestions = (newQuestions = [], goalIndex, questionIndex, ditchedQuestions, cb) => {
    const { questionnaire } = this.state;
    const newQuestionnaire = [...questionnaire];
    newQuestionnaire[goalIndex].questions.splice(questionIndex + 1, 0, ...[...newQuestions]);
    newQuestionnaire[goalIndex].questions[questionIndex].lastSaved = ditchedQuestions;
    this.initializeForm({ questionIndex, questionnaire: newQuestionnaire, ditchedQuestions }, cb);
  };

  removeNewQuestions = (goalIndex, questionIndex, ditchedQuestions, cb) => {
    const { questionnaire, questionsAnswered } = this.state;
    const newQuestionnaire = [...questionnaire];

    if (newQuestionnaire[goalIndex]) {
      for (let i = questionIndex + 1; i < newQuestionnaire[goalIndex].questions.length; ) {
        if (!newQuestionnaire[goalIndex].questions[i].base) {
          newQuestionnaire[goalIndex].questions.splice(i, 1);
        } else {
          break;
        }
      }
    }

    if (questionsAnswered[0]) {
      this.initializeForm(
        {
          questionnaire: newQuestionnaire,
          ditchedQuestions: -newQuestionnaire[goalIndex].questions[questionIndex].lastSaved,
        },
        cb,
      );
    } else {
      this.initializeForm(
        {
          questionnaire: newQuestionnaire,
          ditchedQuestions,
        },
        cb,
      );
    }
  };

  handleQuestionFocus = (ev, index, goalId) => {
    const { questionnaire } = this.state;
    const element = ev.target;
    const len = questionnaire[0].questions.length;

    const currentGoalIndex = questionnaire.findIndex((goal) => goal.value === goalId);
    this.setAllAsBlur(() => {
      this.setQuestionAsFocused(currentGoalIndex, index);
      /** We need to do it with this method (getBoundingClientRect) so we can preserve the
       * right offset even with relative positioned elements.
       */
      const offsetTop = element.getBoundingClientRect().top + window.scrollY;
      if (!iOSSafari && index !== len - 1) {
        window.scrollTo({ top: offsetTop - SCROLL_OFFSET });
      }
    });
  };

  handleQuestionChange = (ev) => {
    const inputId = ev.target.id;
    const inputName = ev.target.name;
    const inputValue = ev.target.value === '' ? null : Number(ev.target.value);

    if (inputName === 'age') {
      this.setState({ userAge: inputValue });
    }
    this.setState({ currentValue: ev.target.value });
    this.setState({ currentInputId: inputId });
  };

  handleQuestionBlur = (ev, { values = [], goalAnswered, goalIndex, goalName }) => {
    const arrValues = Object.keys(values).map((key) => values[key]);
    this.setState((state) => ({
      ...set(lensPath(['questionsAnswered', goalIndex]), arrValues.length, state),
    }));
    this.setGoalAnswers(values, goalName, goalIndex);

    if (goalAnswered) {
      this.setGoalAsAnswered(goalIndex);
    }
  };

  handleFormAnswered = (data, cb) => {
    const { values = [], goalAnswered, goalIndex, goalName } = data;
    const arrValues = Object.keys(values).map((key) => values[key]);
    this.setState((state) => ({
      ...set(lensPath(['questionsAnswered', goalIndex]), arrValues.length, state),
    }));
    this.setGoalAnswers(values, goalName, goalIndex);

    if (goalAnswered) {
      this.setGoalAsAnswered(goalIndex);
    }
    cb();
  };

  handleStepSubmit = (questionId = '') => {
    const { financialDiagnostic, sectionExpenses, expenseMap, income } = this.state;
    const { setGlobalExpenses, setGlobalIncome, macroStep } = this.props;
    const tempObject = financialDiagnostic;
    const { onStepSubmit } = this.props;

    // This is used to prevent missing last number of user input if it's the last question of flow
    if (!document.getElementById(questionId).readOnly) {
      const { goalNames } = this.state;
      tempObject[financialDiagnostic.length - 1][
        questionId.replace(`${goalNames[financialDiagnostic.length - 1]}-`, '')
      ] = document.getElementById(questionId).value;
    }
    setGlobalExpenses(sectionExpenses, macroStep, expenseMap);
    if (income > 0) {
      setGlobalIncome(income);
    }
    // this.setState({ sectionExpenses: 0 })
    onStepSubmit(tempObject);
  };

  handleExpenseChange = (key, value) => {
    const { expenseMap } = this.state;
    const { macroStep } = this.props;
    let expense = key;
    let rate = 1;
    if (key.match('an_')) {
      rate = 1 / 12;
      expense = expense.substr(3);
    } else if (key.match('bw_')) {
      rate = 2;
      expense = expense.substr(3);
    } else if (key.match('mo_')) {
      expense = expense.substr(3);
    }
    const monthlyValue = value * rate;

    const newExpenseMap = expenseMap;
    newExpenseMap[macroStep] = newExpenseMap[macroStep] || {};
    const previousVal = newExpenseMap[macroStep][key] || 0;
    newExpenseMap[macroStep][key] = value;
    this.setState((prevState) => ({
      sectionExpenses: prevState.sectionExpenses - previousVal + monthlyValue,
      expenseMap: newExpenseMap,
    }));
  };

  updateBalance = (questionId) => {
    const { currentValue } = this.state;
    const inputName = questionId.split('-')[1];
    const value =
      typeof currentValue === 'string' ? parseFloat(currentValue.replace(/,/g, '')) : currentValue;
    if (inputName === 'entertainment_exp') {
      this.handleExpenseChange(inputName, value * 4);
    } else if (inputName === 'total_income') {
      this.setState({ income: value });
      this.forceUpdate();
    } else if (inputName !== 'age' && !inputName.match('owed')) {
      this.handleExpenseChange(inputName, value);
    }
  };

  withCommas = (number) => number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');

  goToNextInput = (questionId) => {
    const { inputList, focusedGoal, questionnaire } = this.state;
    const { macroStep } = this.props;
    const currentInputIndex = inputList.findIndex((id) => id === questionId);
    const nextInputName = inputList[currentInputIndex + 1];
    const nextInput = document.querySelector(`#${nextInputName}`);
    if (nextInput) {
      nextInput.focus();
      if (!iOSSafari && nextInput.offsetTop - SCROLL_OFFSET > 0) {
        window.scrollTo({ top: nextInput.offsetTop - SCROLL_OFFSET });
      }
      let typeOfQuestion;
      // If we're on debt or savings (Where there can be more than one "form") we
      // look for the question in all the forms.
      if (macroStep === 2 || macroStep === 5) {
        const question = findQuestionFromId(questionId.split('-')[1], questionnaire);
        typeOfQuestion = question.type;
        if (typeOfQuestion === 'number') {
          this.updateBalance(questionId);
        }
      } else if (macroStep > 2 && macroStep < 5) {
        typeOfQuestion = questionnaire[focusedGoal].questions[currentInputIndex].type;
        if (typeOfQuestion === 'number') {
          this.updateBalance(questionId);
        }
      }
    } else {
      this.setState({ isFinished: true });
      this.handleStepSubmit(questionId);
    }
  };

  handleGoBack = () => {
    const { goBack, macroStep } = this.props;
    let { income } = this.state;
    if (macroStep === 4) {
      income = 0;
    }
    goBack(() => {
      this.setState((_, props) => ({ sectionExpenses: props.globalExpenses, income }));
    });
    this.forceUpdate();
  };

  keyPress = (ev) => {
    const { currentInputId } = this.state;
    if (ev.keyCode === 13 || ev.keyCode === 9) {
      ev.preventDefault();
      this.goToNextInput(currentInputId);
    }
  };

  render() {
    const { questionnaire, isFinished, userAge, income, sectionExpenses, incomeMap } = this.state;
    const {
      title,
      macroStep,
      hasBeenSubmitted,
      answeredSoFar,
      goForward,
      goBack,
      numberOfSteps,
      incomeSet,
      globalExpenses,
      previousTitle,
    } = this.props;
    const overflow = income - sectionExpenses < 0 && income > 0;
    const formMargin = overflow ? '5em' : '0';
    return (
      <StyledDiagnosticFlow>
        <FlowHeader
          goBack={() => {
            this.handleGoBack();
          }}
          goForward={goForward}
          title={`${title}`}
          previousTitle={previousTitle}
          macroStep={macroStep}
          numberOfSteps={numberOfSteps}
          hasBeenAnswered={hasBeenSubmitted}
        />
        <FlowContainer>
          {overflow && (
            <OverflowBanner>
              <Title level={5}>Algo no cuadra...</Title>
              <Text>
                Las cuentas no están cuadrando (-$
                {this.withCommas((income - sectionExpenses) * -1)}
                ).
              </Text>
              <Text>Verifica tus datos.</Text>
            </OverflowBanner>
          )}
          <FlowContent style={{ marginTop: formMargin }}>
            {!hasBeenSubmitted ? (
              <DiagnosticForm
                questionnaire={questionnaire}
                onQuestionFocus={this.handleQuestionFocus}
                onQuestionChange={this.handleQuestionChange}
                onQuestionBlur={this.handleQuestionBlur}
                onStepSubmit={this.handleStepSubmit}
                goToNextInput={this.goToNextInput}
                isFinished={isFinished}
                userAge={userAge}
                getSubmitRef={(ref) => {
                  this.submitContainer = ref;
                }}
                injectNewQuestions={this.injectNewQuestions}
                removeNewQuestions={this.removeNewQuestions}
                handleFormAnswered={this.handleFormAnswered}
              />
            ) : (
              // TODO: Volver a diferenciar entre hasEditedFormSummary ?
              // Si - 1. Quitar "Siguiente" (FlowHeader) 2. Hacer visible un botón de "Guardar y continuar"
              <Fragment>
                <div>{JSON.stringify(answeredSoFar[macroStep - 1])}</div>
              </Fragment>
            )}
          </FlowContent>
        </FlowContainer>
      </StyledDiagnosticFlow>
    );
  }
}

DiagnosticFlow.propTypes = {
  onStepSubmit: PropTypes.func,
  goBack: PropTypes.func,
  goForward: PropTypes.func,
  title: PropTypes.string,
  macroStep: PropTypes.number,
  numberOfSteps: PropTypes.number,
  hasBeenSubmitted: PropTypes.bool,
  answeredSoFar: PropTypes.object,
  globalExpenses: PropTypes.number,
  globalIncome: PropTypes.number,
  setGlobalExpenses: PropTypes.func,
  setGlobalIncome: PropTypes.func,
  currentStep: PropTypes.number,
};

DiagnosticFlow.defaultProps = {
  onStepSubmit: () => {},
  goBack: () => {},
  goForward: () => {},
  title: 'Template Title',
  macroStep: 0,
  numberOfSteps: 7,
  hasBeenSubmitted: false,
  answeredSoFar: {},
  globalExpenses: 0,
  globalIncome: 0,
  setGlobalExpenses: () => {},
  setGlobalIncome: () => {},
  currentStep: 0,
};

export default DiagnosticFlow;
